import { $formats, UIParagraphFormat } from '../../config/formats';
import { $el } from '../ui/elements';
import { uiEqualClassList, uiGetPrefixedClass, uiGetSiblingsAfter, uiGetSiblingsBefore, uiHasPrefixedClass } from '../ui/functions';
import { $style, UIParagraphStyleParam, UIParagraphStyleTypes, UITextStyleParam, UITextStyleTypes } from '../ui/style';
import { UIClassesSpec } from '../ui/types';
import { UIViewAddSpec, UIViewSpec } from '../ui/view';
import { $log } from '../y/debug';

export function selectRange(range: Range) {
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
}

export function select(start_node: Node | UIViewSpec, start_mode?: 'before' | 'after' | 'start' | 'end', end_mode: 'before' | 'after' | 'start' | 'end' | undefined = start_mode, end_node: Node | UIViewSpec = start_node) {
    if('ui_elem' in start_node)
        start_node = start_node.ui_elem;
    if('ui_elem' in end_node)
        end_node = end_node.ui_elem;

    if(!start_mode) {
        start_mode = 'before';
        end_mode = 'after';
    }

    const range = document.createRange();
    if(start_mode == 'before')
        range.setStartBefore(start_node);
    else if(start_mode == 'after')
        range.setStartAfter(start_node);
    else
        range.setStart(start_node, start_mode == 'end' ? ((start_node instanceof Text) ? start_node.length : start_node.childNodes.length) : 0);

    if(end_mode == 'before')
        range.setEndBefore(end_node);
    else if(end_mode == 'after')
        range.setEndAfter(end_node);
    else
        range.setEnd(end_node, end_mode == 'end' ? ((end_node instanceof Text) ? end_node.length : end_node.childNodes.length) : 0);

    selectRange(range);
}

// const has_text_predicate = (n: TreeNode) => n.role == 'text' && (n.left ? n.from : 0) != (n.right ? n.to : n.node.length)
const has_content_predicate = (n: TreeNode) => n.role == 'content' || (n.role == 'text' && (n.left ? n.from : 0) != (n.right ? n.to : n.node.length))

function calcRole(node: Node, parent?: TreeContainerNode): 'container' | 'content' {
    if(!parent) return 'container';
    if(!(node instanceof Element) || parent.kind == 'span') return 'content';
    if(node.tagName == 'SPAN') return 'container';
    if(parent.kind == 'object') return 'content';
    if(node.tagName == 'A') return 'container';
    if(parent.kind == 'paragraph') return 'content';
    return 'container';
}

type ContainerKind = 'group' | 'paragraph' | 'span' | 'object';
function calcKind(node: Element, parent?: TreeContainerNode): ContainerKind {
    if(!parent) return 'group';
    if(node.tagName == 'SPAN') return 'span';
    if(node.tagName == 'A') return 'object';
    return 'paragraph';
}

type SelectionPoint = [Node, number];

interface TreeContainerNode { role: 'container'; kind: ContainerKind, node: Element; children: TreeNode[]; left: boolean; right: boolean; parent?: TreeContainerNode; }
interface TreeTextNodeInside { role: 'text'; node: Text; left: false; right: false; parent: TreeContainerNode; }
interface TreeTextNodeLeft { role: 'text'; node: Text; left: true; right: false; from: number; parent: TreeContainerNode; }
interface TreeTextNodeRight { role: 'text'; node: Text; left: false; right: true; to: number; parent: TreeContainerNode; }
interface TreeTextNodeBoth { role: 'text'; node: Text; left: true; right: true; from: number; to: number; parent: TreeContainerNode; }
interface TreeContentNode { role: 'content'; node: ChildNode; left: false; right: false; parent: TreeContainerNode; }
interface TreeStartNode { role: 'insertion'; after: Node | null; left: true; right: false; parent: TreeContainerNode; }
interface TreeEndNode { role: 'insertion'; before: Node | null; left: false; right: true; parent: TreeContainerNode; }
interface TreeStartEndNode { role: 'insertion'; before: Node | null; after: Node | null; left: true; right: true; parent: TreeContainerNode; }

type TreeTextNode = TreeTextNodeInside | TreeTextNodeLeft | TreeTextNodeRight | TreeTextNodeBoth;
type TreeInsertionNode = TreeStartNode | TreeEndNode | TreeStartEndNode;
type TreeNode = TreeContainerNode | TreeTextNode | TreeContentNode | TreeInsertionNode;

function buildTree(node: Node, root: Node, start: SelectionPoint, end: SelectionPoint) {
    const path_down: ChildNode[] = [];
    while(node != root) {
        const parent = node.parentNode;
        if(parent) {
            path_down.push(node as ChildNode);
            node = parent;
        } else {
            break;
        }
    }

    return buildDown(node, undefined, start, end, path_down) as TreeContainerNode;
}

function buildDown(node: Node, parent: TreeContainerNode | undefined, left: [Node, number] | undefined, right: [Node, number] | undefined, path_down: ChildNode[]): TreeNode {
    if(node instanceof Text)
        return { role: 'text', node, left: !!left, right: !!right, from: left?.[1], to: right?.[1], parent } as TreeNode;

    const role = calcRole(node, parent);

    if(!(node instanceof Element) || role == 'content')
        return { role: 'content', node: node as ChildNode, left: false, right: false, parent: parent! }

    const tree_children: TreeNode[] = [];
    const kind = calcKind(node, parent);

    const tree_node: TreeContainerNode = { role, kind, node, left: !!left, right: !!right, parent, children: tree_children };

    const in_path = path_down.pop();

    let children: ChildNode[];
    let has_end: TreeEndNode | undefined;

    if(in_path) {
        children = [in_path];
    } else {
        children = Array.from(node.childNodes);

        if(node == right?.[0]) {
            if(node == left?.[0] && left[1] == right[1]) {
                tree_children.push({ role: 'insertion', left: true, right: true, before: children[left[1]] || null, after: children[left[1] - 1] || null, parent: tree_node });
                return tree_node;
            }

            has_end = { role: 'insertion', left: false, right: true, before: children[right[1]] || null, parent: tree_node };
            children = children.slice(0, right[1]);
            right = undefined;
        }
        if(node == left?.[0]) {
            tree_children.push({ role: 'insertion', left: true, right: false, after: children[left[1] - 1] || null, parent: tree_node });
            children = children.slice(left[1]);
            left = undefined;
        }
    }

    for(const n of children) {
        if(left && !n.contains(left[0])) continue;

        let in_right = right && (in_path || n.contains(right[0])) ? right : undefined;

        const new_node = buildDown(n, tree_node, left, in_right, path_down);

        if(left && new_node.role == 'content')
            tree_children.push({ role: 'insertion', left: true, right: false, after: n.previousSibling, parent: tree_node });
        tree_children.push(new_node);
        if(in_right && new_node.role == 'content')
            tree_children.push({ role: 'insertion', left: false, right: true, before: n.nextSibling, parent: tree_node });

        if(in_right) break;
        left = undefined;
    }

    if(has_end)
        tree_children.push(has_end);

    return tree_node;
}

function getNodesBefore(node: TreeNode): Node[] {
    if(node.role == 'insertion') {
        if(node.right) {
            return node.before ? uiGetSiblingsBefore(node.before) : Array.from(node.parent.node.childNodes);
        } else {
            return node.after ? uiGetSiblingsBefore(node.after, true) : [];
        }
    } else {
        return uiGetSiblingsBefore(node.node);
    }
}

function getNodesAfter(node: TreeNode): Node[] {
    if(node.role == 'insertion') {
        if(node.left) {
            return node.after ? uiGetSiblingsAfter(node.after) : Array.from(node.parent.node.childNodes);
        } else {
            return node.before ? uiGetSiblingsAfter(node.before, true) : [];
        }
    } else {
        return uiGetSiblingsAfter(node.node);
    }
}

function findNode(node: TreeNode, pred: (n: TreeNode) => boolean): TreeNode | undefined {
    if(pred(node)) return node;
    if(node.role == 'container') {
        for(let n of node.children) {
            const found = findNode(n, pred);
            if(found) return found;
        }
    }

    return undefined;
}

function opSplit(node: TreeNode) {
    const role = node.role;
    if(role == 'container' && node.kind == 'span') {
        const children = node.children;
        if(!children.length) return node;
        print(node, 'Before split children');

        for(const c of children) {
            opSplit(c);
        }

        print(node, 'After split children');
        if(node.left) {
            const first = children[0];
            const before = getNodesBefore(first);
            if(before.length) {
                node.node.before($el.create(node.node.tagName, node.node.className, before));
                if(first.role == 'insertion' && first.left)
                    first.after = null;
            }
        }

        print(node, 'Blah');
        if(node.right) {
            const last = children[children.length - 1];
            const after = getNodesAfter(last);
            if(after.length) {
                node.node.after($el.create(node.node.tagName, node.node.className, after));
                if(last.role == 'insertion' && last.right)
                    last.before = null;
            }
        }
        print(node, 'Bloh');
    } else if(role == 'text') {
        print(node, 'Splitting text');
        if(node.right) { //it contains the end point
            if(node.to == 0) { //no part of the text is selected
                if(node.left)
                    Object.assign(node, { role: 'insertion', before: node.node, after: node.node.previousSibling });
                else
                    Object.assign(node, { role: 'insertion', before: node.node });
                return;
            }
            if(node.to != node.node.length) {
                node.node.splitText(node.to); //split it and keep the first part in the selection
            }
        }
        if(node.left) {
            if(node.from == node.node.length) { //no part of the text is selected
                if(node.right)
                    Object.assign(node, { role: 'insertion', before: node.node.nextSibling, after: node.node });
                else
                    Object.assign(node, { role: 'insertion', after: node.node });
                return;
            }
            if(node.from != 0) {
                node.node = node.node.splitText(node.from); //split it and keep the second part in the selection
                node.from = 0;
                if(node.right)
                    node.to = node.node.length;
            }
        }
        print(node, 'Split text');
    }

    return node;
}

function opRemove(node: TreeNode, inserting?: boolean) {
    if(!node.left && !node.right) {
        node.node.remove();
        return;
    }

    const role = node.role;

    if(role == 'text') {
        const text = node.node.textContent || '';
        const new_text = text.slice(0, node.left ? node.from : 0) + text.slice(node.right ? node.to : text.length);
        if(new_text) {
            node.node.textContent = new_text;
            if(node.right)
                node.to = node.left ? node.from : 0;
        } else {
            let ret: TreeNode;
            if(!node.left) {
                ret = { role: 'insertion', left: false, right: true, before: node.node.nextSibling, parent: node.parent };
            } else if(!node.right) {
                ret = { role: 'insertion', left: true, right: false, after: node.node.previousSibling, parent: node.parent };
            } else {
                ret = { role: 'insertion', left: true, right: true, before: node.node.nextSibling, after: node.node.previousSibling, parent: node.parent };
            }
            node.node.remove();
            return ret;
        }
    } else if(role == 'container') {
        const children = node.children;

        const must_merge = node.kind == 'group' && children.length > 1 && (!inserting || findNode(children[children.length - 1], has_content_predicate));

        for(let i = 0; i < children.length; i++) {
            const child = opRemove(children[i]);
            if(child) {
                children[i] = child;
            } else {
                children.splice(i, 1);
                i--;
            }
        }

        if(must_merge && children.length == 2)
            opMergeContainer(children[0] as TreeContainerNode, children[1] as TreeContainerNode);
    }

    return node;
}

function opInsert(node: TreeNode, content: UIViewAddSpec) {
    if(!node.left) throw new Error('Invalid tree');

    if(node.role == 'insertion') {
        node.after = node.parent.node.ui_insert(content, node.after, true);
    } else if(node.role == 'text') {
        if(node.from) {
            opSplit(node);
            opInsert(node, content);
        } else {
            node.parent.node.ui_insert(content, node.node);
        }
    } else {
        opInsert(node.children[0], content);
    }
}

function opUnwrap(node: TreeContainerNode) {
    const children = node.children;
    if(children.length) {
        const first = children[0];
        const last = children[children.length - 1];
        if(first.role == 'insertion' && first.left)
            first.after = node.node.previousSibling;
        if(last.role == 'insertion' && last.right)
            last.before = node.node.nextSibling;
    }

    for(const c of children) {
        c.parent = node.parent;
    }

    node.node.replaceWith(...node.node.childNodes);
    node.parent!.children.splice(node.parent!.children.indexOf(node), 1, ...children);
}

function opMergeText(a: TreeTextNode, b: TreeTextNode) {
    print(a, 'Before merge text A');
    print(b, 'Before merge text B');
    if(a.left)
        Object.assign(b, { left: true, from: a.from });
    if(b.right)
        b.to += a.node.length;
    b.node.textContent = (a.node.textContent || '') + (b.node.textContent || '');
    a.parent.node.removeChild(a.node);
    a.parent.children.splice(a.parent.children.indexOf(a), 1);
}

function opMergeContainer(a: TreeContainerNode, b: TreeContainerNode) {
    print(a, 'Before merge A');
    print(b, 'Before merge B');
    a.right = b.right;
    const children = b.children;
    for(let i = 0; i < children.length; i++) {
        const child = children[i];
        child.parent = a;
        a.children.push(child);
    }
    a.node.append(...b.node.childNodes);
    b.parent!.children.splice(b.parent!.children.indexOf(b), 1);
    b.node.remove();
    print(a, 'Before normalize');
    opNormalize(a);
}

function opNormalize(container: TreeContainerNode) {
    const children = container.children;

    for(let i = 0; i < children.length; i++) {
        const child = children[i];

        if(i == 0 && child.role != 'insertion') {
            const previous = child.node.previousSibling;
            if(previous) {
                if(child.role == 'text') {
                    if(previous instanceof Text) {
                        const text = previous.textContent || '';
                        child.node.textContent = text + (child.node.textContent || '');
                        container.node.removeChild(previous);
                        if(child.left)
                            child.from += text.length;
                        if(child.right)
                            child.to += text.length;
                        i--;
                        continue;
                    }
                } else if(child.role == 'container' && child.kind == 'span') {
                    if(previous instanceof Element && previous.tagName == 'SPAN' && uiEqualClassList(child.node, previous)) {
                        const nodes = Array.from(previous.childNodes);
                        if(nodes.length && child.left) {
                            const first = child.children[0];
                            if(first.role == 'insertion' && first.left && !first.after)
                                first.after = nodes[nodes.length - 1];
                        }
                        child.node.prepend(...nodes);
                        container.node.removeChild(previous);
                        i--;
                        continue;
                    }
                }
            }
        }

        if(i == children.length - 1 && child.role != 'insertion') {
            const next = child.node.nextSibling;
            if(next) {
                if(child.role == 'text') {
                    if(next instanceof Text) {
                        child.node.textContent = (child.node.textContent || '') + (next.textContent || '');
                        container.node.removeChild(next);
                        i--;
                        continue;
                    }
                } else if(child.role == 'container' && child.kind == 'span') {
                    if(next instanceof Element && next.tagName == 'SPAN' && uiEqualClassList(child.node, next)) {
                        const nodes = Array.from(next.childNodes);
                        if(nodes.length && child.right) {
                            const last = child.children[child.children.length - 1];
                            if(last.role == 'insertion' && last.right && !last.before)
                                last.before = nodes[0];
                        }
                        child.node.append(...nodes);
                        container.node.removeChild(next);
                        i--;
                        continue;
                    }
                }
            }
        }

        if(child.role == 'container' && child.kind == 'span' && child.node.className == '') {
            opUnwrap(child);
            i--;
            continue;
        }

        if(i > 0) {
            const previous = children[i - 1];
            if(child.role == 'text') {
                if(previous.role == 'text') {
                    opMergeText(previous, child);
                    print(container, 'After merge text');
                    i--;
                    continue;
                }
            } else if(child.role == 'container' && child.kind == 'span') {
                if(previous.role == 'container' && previous.kind == 'span' && uiEqualClassList(previous.node, child.node)) {
                    opMergeContainer(previous, child);
                    print(container, 'After merge');
                    i--;
                    continue;
                }
            }
        }
    }

    print(container, 'After normalize');
}

function insertNodeBefore(node: Node, tree_node: TreeNode) {
    if(tree_node.role == 'insertion') {
        if(tree_node.right)
            tree_node.parent.node.insertBefore(node, tree_node.before);
        else
            tree_node.parent.node.insertBefore(node, tree_node.after ? tree_node.after.nextSibling : tree_node.parent.node.firstChild);
    } else {
        tree_node.node.before(node);
    }
}

function opWrapWithSpan(nodes: TreeNode[]) {
    const span = $el.SPAN();
    const span_node: TreeContainerNode = {
        role: 'container',
        kind: 'span',
        node: span,
        children: nodes,
        left: nodes[0].left,
        right: nodes[nodes.length - 1].right,
    }

    insertNodeBefore(span, nodes[0]);

    for(const n of nodes) {
        if(n.role == 'insertion') {
            if(n.left)
                n.after = null;
            if(n.right)
                n.before = null;
        } else {
            span.appendChild(n.node);
        }

        n.parent = span_node;
    }

    if(span.firstChild == null) {
        // TODO: find a really good solution
        // we've created an empty span with the selection inside... this breaks in chrome, so we need some hacks
        // span.appendChild(document.createTextNode('\u200b'));
        // span.classList.add('t-empty-hack');
    }

    return span_node;
}

function applyStyleToContainer<P extends UITextStyleParam>(container: TreeContainerNode, root: Element, style: P, value: undefined | UITextStyleTypes[P]) {
    const children = container.children;

    let changed = false;

    for(let i = 0; i < children.length; i++) {
        let child = children[i];

        if(child.role == 'container') {
            if(child.kind == 'span') {
                if(!$style.spanHas(child.node, container.node, root, style, value)) {
                    opSplit(child);
                    $style.spanSet(child.node, root, style, value);
                    changed = true;
                }
            } else {
                applyStyleToContainer(child, root, style, value);
            }
        } else if(!$style.spanHas(undefined, container.node, root, style, value)) {
            let nodes: TreeNode[] = [];

            while(child && (child.role == 'content' || child.role == 'text' || child.role == 'insertion')) {
                opSplit(child);
                nodes.push(child);
                children.splice(i, 1);
                child = children[i];
            }

            if(nodes.length) {
                const span = opWrapWithSpan(nodes);
                span.parent = container;
                children.splice(i, 0, span);
                $style.spanSet(span.node, root, style, value);
                changed = true;
            }
        }
    }

    if(changed) {
        print(container, 'Before normalize');
        opNormalize(container);
    }
}

function getStyleRef(container: TreeContainerNode): [Element | undefined, Element | undefined] {
    for(const c of container.children) {
        if(c.role == 'container' && c.left) {
            if(c.kind == 'span')
                return [c.node, container.node];
            return getStyleRef(c);
        } else if(c.role == 'text' && (c.left ? c.from : 0) != (c.right ? c.to : c.node.length)) {
            return [undefined, container.node];
        }
    }

    return [undefined, container.node];
}

function setRangeStart(range: Range, node: TreeContainerNode) {
    const first = node.children[0];
    if(!first || !first.left) throw new Error('Invalid tree');

    if(first.role == 'text') {
        range.setStart(first.node, first.from);
    } else if(first.role == 'insertion') {
        if(first.after)
            range.setStartAfter(first.after);
        else
            range.setStart(node.node, 0);
    } else {
        setRangeStart(range, first);
    }
}

function setRangeEnd(range: Range, node: TreeContainerNode) {
    const last = node.children[node.children.length - 1];
    if(!last || !last.right) throw new Error('Invalid tree');

    if(last.role == 'text') {
        range.setEnd(last.node, last.to);
    } else if(last.role == 'insertion') {
        if(last.before)
            range.setEndBefore(last.before);
        else
            range.setEnd(node.node, node.node.childNodes.length);
    } else {
        setRangeEnd(range, last);
    }
}

// function getRight(container: TreeContainerNode): [Node, number]
// {
//     const c = container.children[container.children.length-1];
//     if(c?.right) {
//         if(c.role == 'container' || c.role == 'span')
//             return getRight(c);
//         else if(c.role == 'text')
//             return [c.node, c.to ?? 0];
//     }

//     return [container.node, container.end ?? 0];
// }

function print(node: TreeNode, pre_prefix: string = '', depth: number = 0) {
    let prefix = '%c' + (pre_prefix ? pre_prefix.slice(0, 22).padEnd(24) : '') + '%c' + (node.role == 'container' ? node.kind : node.role).padEnd(12) + (node.left ? 'left' : '').padEnd(6) + (node.right ? 'right' : '').padEnd(6) + ' '.repeat(depth * 2);
    let pre_style = ['color: #66ACFF', ''];

    if(node.role == 'text') {
        const text = node.node.textContent || '';
        const from = node.left ? node.from : 0;
        const to = node.right ? node.to : text.length;
        $log('selection', `${prefix}%c"${text.slice(0, from)}%c${text.slice(from, to) || (node.left || node.right ? '|' : '')}%c${text.slice(to)}" ${node.left ? node.from : ''} ${node.right ? node.to : ''}`, ...pre_style, 'color: #888;', 'color: #fff; font-weight: bold;', 'color: #888;');
    } else if(node.role == 'insertion') {
        $log('selection', `${prefix}%cInsertion`, ...pre_style, 'color: #E13F3F;');
    } else if(node.role == 'container') {
        $log('selection', prefix + '%c<%c' + node.node.tagName.toLowerCase() + '%c' + (node.node.className ? ' ' + node.node.className : '') + '%c>', ...pre_style, 'color: #AB9DF2', 'color: #AB9DF2; font-weight: bold;', 'color: #9DF2AB', 'color: #AB9DF2');
        node.children.forEach(child => print(child, pre_prefix, depth + 1));
    } else {
        $log('selection', `${prefix}%c%o"`, ...pre_style, 'color: #888;', node.node);
    }
}

class Selection {
    valid: boolean;
    private _tree!: TreeContainerNode;
    // private _focus_end!: boolean;

    constructor(root: HTMLElement) {
        this.valid = false;
        const sel = window.getSelection();
        if(!sel || !sel.rangeCount) return;
        const range = sel.getRangeAt(0);
        if(!range) return;
        const origin = range.commonAncestorContainer;
        if(!root.contains(origin)) return;

        this.valid = true;
        // this._focus_end = sel.focusNode == range.endContainer;

        $log('selection', '------------------------------------------------------------------------')
        $log('selection', '%d %d --> %d %d', range.startContainer, range.startOffset, range.endContainer, range.endOffset);
        $log.tick('parsing selection');
        this._tree = buildTree(origin, root, [range.startContainer, range.startOffset], [range.endContainer, range.endOffset]);
        $log.tock('selection', 'parsing selection');
        $log('selection', '------------------------------------------------------------------------')
        this.print('Parsing');
    }

    select() {
        const range = new Range;

        setRangeStart(range, this._tree);
        setRangeEnd(range, this._tree);
        selectRange(range);
    }

    hasContent() {
        return !!findNode(this._tree, has_content_predicate)
    }

    getContainerWithTag(tag: 'A', kind?: ContainerKind) {
        return findNode(this._tree, n => n.role == 'container' && (!kind || n.kind == kind) && n.node.tagName == tag) as TreeContainerNode | undefined;
    }

    wrapWith(maker: (content: UIViewAddSpec) => HTMLElement): HTMLElement[] {
        const created: HTMLElement[] = [];

        for(const p of this._tree.children!) {
            // TODO: find all the paragraphs... we're doing this on the paragraphs
            if(p.role != 'container') continue;
            const children = p.children;
            const nodes: Node[] = [];
            for(let i = 0; i < children.length; i++) {
                const child = children[i];
                opSplit(child);
                if(child.role != 'insertion')
                    nodes.push(child.node);
            }

            if(nodes.length) {
                const where = nodes[nodes.length - 1].nextSibling;
                const wrapper = maker(nodes);
                p.node.insertBefore(wrapper, where);
                created.push(wrapper);
            }
        }

        return created;
    }

    unwrap(node: TreeContainerNode) {
        opUnwrap(node);
        this.select();
    }

    remove(inserting?: boolean) {
        const tree = this._tree;
        opRemove(tree, inserting);
        this.print('Removed');
        this.select();
    }

    insert(content: UIViewAddSpec) {
        opRemove(this._tree, true);
        opInsert(this._tree, content);
        this.print('Inserted');
        this.select();
    }

    setParagraphStyle<P extends UIParagraphStyleParam>(param: P, value: undefined | UIParagraphStyleTypes[P]) {
        const children = this._tree.children;

        for(const c of children) {
            if(c.role == 'container' && (c.children.length || children.length == 1)) {
                const classes: UIClassesSpec = [];
                let f = (param == 'f' ? value : uiGetPrefixedClass(c.node, 'p-f')) as UIParagraphFormat | undefined;
                if(f && $formats[f]?.classes)
                    classes.push($formats[f].classes);
                const a = param == 'a' ? value : uiGetPrefixedClass(c.node, 'p-a');
                if(a)
                    classes.push('p-a-' + a);
                const l = param == 'l' ? value : uiGetPrefixedClass(c.node, 'p-l');
                if(l)
                    classes.push('p-l-' + l);
                if(c.node.childNodes.length == 0)
                    classes.push('ed-empty-paragraph');
                c.node.className = $el.flattenClasses(classes) || '';
            }
        }
    }

    hasParagraphStyle<P extends UIParagraphStyleParam>(param: P, value: UIParagraphStyleTypes[P]) {
        return !!findNode(this._tree, n => n.role == 'container' && n.kind == 'paragraph' && uiHasPrefixedClass(n.node, 'p-' + param, value));
    }

    getParagraphStyle<P extends UIParagraphStyleParam>(param: P): UIParagraphStyleTypes[P] | undefined {
        const ref = findNode(this._tree, n => n.role == 'container' && n.kind == 'paragraph' && (n.children.length > 0 || n.parent?.children.length == 1)) as TreeContainerNode | undefined;
        return ref && uiGetPrefixedClass(ref.node, 'p-' + param) as any;
    }

    hasTextStyle<P extends UITextStyleParam>(style: P, value: UITextStyleTypes[P] | undefined) {
        return $style.spanHas(...getStyleRef(this._tree), this._tree.node, style, value);
    }

    getTextStyle<P extends UITextStyleParam>(style: P): UITextStyleTypes[P] | undefined {
        return $style.spanGet(...getStyleRef(this._tree), this._tree.node, style);
    }

    setTextStyle<P extends UITextStyleParam>(style: P, value: undefined | UITextStyleTypes[P]) {
        for(const p of this._tree.children!) {
            if(p.role != 'container') continue;
            applyStyleToContainer(p, this._tree.node, style, value);
        }

        this.select();
    }

    print(prefix?: string) {
        print(this._tree, prefix);
    }
}

export function getSelection(root: UIViewSpec) {
    const selection = new Selection(root.ui_elem);
    return selection.valid ? selection : undefined;
}

export type EDSelection = Selection;
